import React from "react"
import {graphql} from "gatsby"
// eslint-disable-next-line no-unused-vars
import tw, {styled} from "twin.macro"

import Layout from "components/layout"
import SEO from "components/seo"

import ProductionStrategyProductSelector from "components/production-strategy-product-selector"
import Divider from "components/divider"
import ProductionStrategyProductsHeader from "components/production-strategy-products-header"
import SlantedSection from "components/sections/slant"
import BgDots from "images/bg/demand360-product.jpg"
import {getSharePreviewUrls} from "utils/share-preview"
import ProductionPlannerContactUs from "components/production-planner-contact-us"
import ProductVideoSection from "components/sections/product-video-section"
import ProductionPlannerFeatures from "components/sections/productionplanner-features"
import FaqSection from "components/faq-section"
import {HeaderBackground} from "../components/product-page-header";

export const Title = styled.h2`
  ${tw`py-2 w-full font-display font-bold text-white text-center m:text-left`}
  font-size: 25px;
  line-height: 25px;
  @media (min-width: 640px) {
    font-size: 35px;
    line-height: 35px;
  }
  @media (min-width: 1024px) {
    font-size: 44px;
    line-height: 55px;
  }

  &.product {
    ${tw`w-auto`}
  }

  &.product-profile {
    ${tw`mb-3`}
  }
`

export const Body = styled.div`
  h2 {
    ${tw`font-display text-yellow`}
    font-size: 18px;
    line-height: 20px;

    @media (min-width: 640px) {
      line-height: 27px;
    }
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  li {
    padding-left: 1em;
    text-indent: -0.7em;
  }

  li > div {
    display: inline;
  }

  li::before {
    content: "• ";
    color: #f7bc2d;
  }

  &.product {
    li::before {
      color: #ffffff;
    }

    h2 {
      ${tw`mb-12`}
    }
  }
`

const HeaderStyles = styled.div`
  ${tw`mx-auto m:max-w-m l:max-w-l text-center`}

  @media (min-width: 1200px) {
    max-width: 900px;
  }

  ${Title} {
    ${tw`text-center`}
  }

  ${Body} {
    ${tw`mx-auto`}

    font-size: 16px;
    max-width: 800px;

    @media (min-width: 1200px) {
      font-size: 18px;
    }
  }

  .video {
    .gradient {
      ${tw`hidden`}
    }
  }
`


const Main = styled.div`
  .audience-insights-wrapper {
    ${tw`mx-auto max-w-s m:max-w-m l:max-w-l mb-20 m:mb-10`}

    &.extra-content {
      ${tw`m:mb-0`}
    }
  }
`

const PageHeaderContentStyles = styled.div`
  ${tw`pt-16 pb-32 text-center`}

  &.product, &.product-group {
    ${tw`pb-10 l:pb-0`}

    p {
      font-size: 18px;
      line-height: 30px;
    }
  }

  &.solutions {
    ${tw`pb-0`}
  }

  &.product {
    ${tw`pt-24 mb-6 l:mb-10`}
  }

  &.product-group {
    ${tw`mx-auto mt-0 mb-4 m:mb-0 l:mb-16 pt-32`}

    max-width: 900px;

    &.press-coverage {
      ${tw`pt-32 l:pt-44`}
    }
  }

  h1 {
    ${tw`py-2 font-display text-white`}
    font-size: 30px;
    line-height: 35px;
    @media (min-width: 640px) {
      font-size: 45px;
      line-height: 53px;
    }
    @media (min-width: 1024px) {
      font-size: 60px;
      line-height: 79px;
    }
  }

  h3 {
    ${tw`font-display text-yellow `}
    font-size: 14px;
    line-height: 20px;
    @media (min-width: 640px) {
      font-size: 18px;
      line-height: 25px;
    }
  }

  p {
    ${tw`font-body text-white`}
    font-size: 1.125rem;
    line-height: 1.5;
    text-align: center;
    margin-top: 0.25rem;
    @media (min-width: 640px) {
      font-size: 1.25rem;
      line-height: 1.5;
      margin-top: 0.75rem;
    }
  }
`


const Container = styled.div`
  ${tw`mx-auto relative`}

  background: linear-gradient(0deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)),
    ${({ bg }) => `url(${bg})`};
  background-repeat: repeat-y;
  background-position: center;
  background-size: 100%;
  background-color: black;
`

const ProductFeatureStyles = styled.div`
  ${tw`text-white text-left flex flex-col-reverse l:flex-row odd:l:flex-row-reverse`}

  &.odd {
    .info {
      ${tw`l:pr-0 l:pl-10`}
    }
  }

  .info {
    ${tw`w-full l:w-1/2 mt-6 l:mt-0 l:pr-10`}

    .title {
      font-weight: 800;
      font-size: 32px;
      line-height: 40px;

      @media (min-width: 640px) {
        font-size: 44px;
        line-height: 55px;
      }
    }

    .body {
      ${tw`mt-5`}

      font-size: 18px;
      line-height: 30px;
    }
  }
`

function ProductFeature({ feature, index }) {
  const { featuredImage, headerContent, mainContent } = feature
  const odd = (index + 1) % 2 !== 0

  return (
    <ProductFeatureStyles className={odd ? "odd" : ""}>
      <div className="info">
        <div
          className="title"
          dangerouslySetInnerHTML={{
            __html: `<div>${headerContent.html}</div>`,
          }}
        />
        <div
          className="body"
          dangerouslySetInnerHTML={{ __html: `<div>${mainContent.html}</div>` }}
        />
      </div>
      {/* <div className="hidden l:block l:w-1/12" /> */}
      <div className="w-full l:w-1/2 flex flex-col rounded">
        {/*eslint-disable-next-line jsx-a11y/alt-text*/}
        <img src={featuredImage.url} className="rounded object-cover" />
      </div>
    </ProductFeatureStyles>
  )
}

const StreamingEconomicsProduct = ({ data }) => {
  const { gcms } = data
  const { page } = data.gcms
  const { contentBlocks } = page
    const { demand360, contactUs } = gcms

  const { headerImg, headerContent, extraData } = page
  const content = headerContent?.html
  const { colors = {} } = extraData

  const contactUsCb = contactUs.find(
      ({ identifier }) => identifier === "production-planner-contact-us-section-header"
  )

  const headerSectionContent = contentBlocks.find(
      ({ identifier }) => identifier === "production-planner-header-section"
  )

  const features = contentBlocks.filter(({ identifier }) => !identifier)
  const videoSection = contentBlocks.find(
    ({ identifier }) => identifier === "video-section-block"
  )

    const { featuredPressArticles, featuredLogosClients, featuredVideo } =
        demand360

    const extraContentBlocks = contentBlocks.filter(({ identifier }) =>
    identifier?.endsWith("-extra-content")
  )

    const hasExtraCbs = extraContentBlocks.length > 0

    const faqSchema = page.faq && page.faq.length > 0 ? [
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": page.faq.map((faq) => ({
            "@type": "Question",
            "name": faq.question.html.replace(/<[^>]*>/g, ""),
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer.html,
            },
          })),
        }),
      },
    ] : []


    return (
    <Layout>
      {!!page && (
          // eslint-disable-next-line react/jsx-pascal-case
          <SEO
          title={page.title}
          description={page.description}
          path={page.path}
          script={faqSchema}
          sharePreviewUrls={getSharePreviewUrls(page)}
        />
      )}
      <ProductionStrategyProductsHeader current={page.path} />
      {/*<ProductPageHeader page={page} />*/}

      <HeaderBackground {...colors}>
        <div className="center-content">
          {content && (
              <PageHeaderContentStyles
                  className="product"
                  dangerouslySetInnerHTML={{ __html: `<div>${content}</div>` }}
              />
          )}
        </div>
        {headerImg && (
            <div className="hero">
              <img src={headerImg.url} />
            </div>
        )}
      </HeaderBackground>

      <ProductionStrategyProductSelector current={page.path} />
      <Main className="text-white">

        <div className="mt-4 l:mt-8 mb-4 l:mb-8">
          <ProductionPlannerFeatures />
        </div>

        <Container bg={BgDots}>
          <HeaderStyles className="pt-10 mt-4 mb-6">
            {headerSectionContent.headerContent?.text && (
                <Title className="mb-4 product text-center title">
                  {headerSectionContent.headerContent?.text}
                </Title>
            )}
            {headerSectionContent.mainContent?.html && (
                <Body
                    dangerouslySetInnerHTML={{
                      __html: `<div>${headerSectionContent.mainContent?.html}</div>`,
                    }}
                    className="text-center body"
                />
            )}
          </HeaderStyles>
          <div className="pt-8 m:pt-12 px-2 flex flex-wrap justify-start items-start mx-auto max-w-s m:max-w-m l:max-w-l space-y-40 pb-24 l:pb-28 relative z-10">
            {features.map((feature, index) => (
              <ProductFeature
                feature={feature}
                index={index}
                key={feature.id}
              />
            ))}
          </div>


            {videoSection && (
                <ProductVideoSection
                    className={`${hasExtraCbs ? "mb-20" : "mb-40 m:mb-48"}`}
                    contentBlock={videoSection}
                />
            )}

          {page.faq && page.faq.length > 0 && (
              <div className="mx-auto max-w-s m:max-w-m l:max-w-l mt-24 l:mt-20 pb-2 l:pb-4 mb-20 l:mb-36 px-2">
                  <FaqSection faqs={page.faq} />
              </div>
          )}

          {contactUsCb && (
              <SlantedSection id="form">
                <ProductionPlannerContactUs
                    contentBlock={contactUsCb}
                    modalContentBlock={{}}
                    highlights={[]}
                />
              </SlantedSection>
          )}


            {/*{hasExtraCbs && (*/}
            {/*    <div className="mb-24">*/}
            {/*        {extraContentBlocks.map((cb) => (*/}
            {/*            <FeaturedContent cb={cb} className="on-list" key={cb.id} />*/}
            {/*        ))}*/}
            {/*    </div>*/}
            {/*)}*/}



            {/*{featuredLogosClients && (*/}
            {/*    <div className="mx-auto max-w-s m:max-w-m l:max-w-l mt-24 l:mt-20">*/}
            {/*        <PartnerLogos data={featuredLogosClients} />*/}
            {/*    </div>*/}
            {/*)}*/}

            {/*<div className="mx-auto max-w-s m:max-w-m l:max-w-l">*/}
            {/*    <ExploreOtherProducts*/}
            {/*        className="mt-5"*/}
            {/*        page={page.path.split("/").pop()}*/}
            {/*    />*/}
            {/*</div>*/}
            {/*<div className="py-32 m:pt-48 px-2 flex flex-col justify-around items-center content-center mx-auto max-w-s m:max-w-m l:max-w-l min-h-screen">*/}
            {/*    <IndustryBubbles />*/}
            {/*</div>*/}

            {/*{featuredVideo && (*/}
            {/*    <div className="mt-12">*/}
            {/*        <FeaturedVideo video={featuredVideo} />*/}
            {/*    </div>*/}
            {/*)}*/}

            {/*{featuredPressArticles?.length > 0 && (*/}
            {/*    <SlantedSection className="pt-0 l:pt-14 z-10 pb-40">*/}
            {/*        <FeaturedPressCarousel*/}
            {/*            className="dark"*/}
            {/*            articles={featuredPressArticles}*/}
            {/*        />*/}
            {/*    </SlantedSection>*/}
            {/*)}*/}

        </Container>
      </Main>
    </Layout>
  )
}

export const query = graphql`
  query StreamingEconomicsProductPage($pagePath: String) {
    gcms {
      page(where: { path: $pagePath }) {
        ...PageData
        headerImg: headerImage {
             url(transformation: { image: { resize: { width: 1200 } } })
        }
        faq {
          ... on GCMS_FrequentlyAskedQuestion {
            id
            title
            question {
              html
            }
            answer {
              html
            }
          }
        }
        sortedBlocks: contentBlocks {
          id
          title
          identifier
          headerContent {
            html
            text
          }
          mainContent {
            html
          }
          featuredImage {
            mimeType
            handle
            url
          }
          extraData
          solutionsProduct {
            description
            features
          }
          linkUrl
          label
          linkTargetBlank
          callToAction
          youtubeId
        }
        insightsArticleSerieses {
          identifier
          insightsArticles {
            ...InsightsData
            pressLogo {
              url(
                transformation: {
                  image: { resize: { width: 250, fit: clip } }
                  document: { output: { format: webp } }
                }
              )
            }
          }
        }
      }
      contactUs: contentBlocks(
        where: { identifier: "production-planner-contact-us-section-header" }
      ) {
        id
        identifier
        headerContent {
          html
        }
      }
      demand360: page(where: { path: "/products/demand360" }) {
        ...PageData
      }
    }
  } 
`

export default StreamingEconomicsProduct
